import { z } from "zod";
import type {
  Character,
  Encounter,
  Location,
  Objective,
  SideQuest,
  Skill,
  StorySeedDrafts,
} from "@/types/story.ts";

type DraftKey = keyof StorySeedDrafts;

const SECTION_KEYS: DraftKey[] = [
  "storyName",
  "mainObjective",
  "storyTwist",
  "storyIntro",
  "themeVariant",
  "sideQuests",
  "encounters",
  "skills",
  "characters",
  "locations",
];

const skillSchema = z.object({
  name: z.string(),
  emoji: z.string(),
  application: z.string().optional(),
});

const objectiveSchema = z.object({
  description: z.string(),
  skillUses: z.array(z.string()).optional().default([]),
});

const characterSchema = z.object({
  name: z.string(),
  role: z.string(),
  emoji: z.string(),
  skillUses: z.array(z.string()).optional().default([]),
});

const locationSchema = z.object({
  name: z.string(),
  description: z.string(),
  emoji: z.string(),
  skillUses: z.array(z.string()).optional().default([]),
});

const sideQuestSchema = z.object({
  description: z.string(),
  skillUses: z.array(z.string()).optional().default([]),
});

const encounterSchema = z.object({
  description: z.string(),
  type: z.string().optional(),
});

const elementSchemas: Record<DraftKey, z.ZodTypeAny> = {
  storyName: z.string(),
  mainObjective: objectiveSchema,
  storyTwist: z.string(),
  storyIntro: z.string(),
  themeVariant: z.string(),
  sideQuests: sideQuestSchema,
  encounters: encounterSchema,
  skills: skillSchema,
  characters: characterSchema,
  locations: locationSchema,
};

export function parseIncrementalStoryDrafts(
  partialJson: string,
): { drafts: Partial<StorySeedDrafts> } {
  const drafts: Partial<StorySeedDrafts> = {};
  const parsed = parsePartialJson(partialJson);
  if (!parsed || typeof parsed !== "object") return { drafts };

  for (const key of SECTION_KEYS) {
    const value = (parsed as Record<string, unknown>)[key];
    if (!Array.isArray(value)) continue;
    const validatedItems: unknown[] = [];
    const schema = elementSchemas[key];

    for (const item of value) {
      const validation = schema.safeParse(item);
      if (!validation.success) continue;
      const data = validation.data as unknown;
      if (typeof data === "string" && !data.trim()) continue;
      validatedItems.push(data);
    }

    if (validatedItems.length === 0) continue;

    if (key === "storyName" || key === "storyTwist" || key === "storyIntro") {
      drafts[key] = { options: validatedItems as string[] };
    } else if (key === "mainObjective") {
      drafts.mainObjective = { options: validatedItems as Objective[] };
    } else if (key === "themeVariant") {
      drafts.themeVariant = { options: validatedItems as string[] };
    } else if (key === "sideQuests") {
      drafts.sideQuests = { options: validatedItems as SideQuest[] };
    } else if (key === "encounters") {
      drafts.encounters = { options: validatedItems as Encounter[] };
    } else if (key === "skills") {
      drafts.skills = { options: validatedItems as Skill[] };
    } else if (key === "characters") {
      drafts.characters = { options: validatedItems as Character[] };
    } else if (key === "locations") {
      drafts.locations = { options: validatedItems as Location[] };
    }
  }

  return { drafts };
}

function parsePartialJson(input: string): unknown | null {
  const start = input.indexOf("{");
  if (start === -1) return null;
  let attempt = input.slice(start).trim();

  const tryParse = (text: string): unknown | null => {
    const cleaned = closeOpenStructures(text);
    try {
      return JSON.parse(cleaned);
    } catch {
      return null;
    }
  };

  let parsed = tryParse(attempt);
  if (parsed) return parsed;

  for (let i = 0; i < 200 && attempt.length > 0; i++) {
    attempt = attempt.slice(0, -1);
    parsed = tryParse(attempt);
    if (parsed) return parsed;
  }

  return null;
}

function closeOpenStructures(input: string): string {
  let attempt = input.replace(/,\s*([}\]])/g, "$1");

  const openBraces = (attempt.match(/{/g) || []).length;
  const closeBraces = (attempt.match(/}/g) || []).length;
  const openBrackets = (attempt.match(/\[/g) || []).length;
  const closeBrackets = (attempt.match(/\]/g) || []).length;

  if (openBrackets > closeBrackets) {
    attempt += "]".repeat(openBrackets - closeBrackets);
  }
  if (openBraces > closeBraces) {
    attempt += "}".repeat(openBraces - closeBraces);
  }

  return attempt;
}
